"use client";

import { formatMoneyFromString } from "@/utils/format";
import type { ResolvedPrice } from "@/stores/strategiesStore";

interface PriceBadgeProps {
  resolvedPrice: ResolvedPrice | null | undefined;
}

/**
 * PriceBadge (SFWL-014)
 *
 * Renders a strategy's viewer-resolved monthly price + tier label. Renders
 * nothing when `resolvedPrice` is null/undefined — the resolver's fail-safe
 * contract is "no pricing row → hide the price UI", not "show $0". Amounts
 * are pre-computed decimal strings from the API; formatted via
 * `formatMoneyFromString` (thousands separators only, never `parseFloat`).
 *
 * Active promo (reseller-retail only, per locked spec) renders the original
 * `amountMonthly` struck through next to the server-computed
 * `amountAfterPromo`, a code chip, and a compact expiry ("ends Sep 1") — no
 * discount math happens client-side.
 */

/**
 * Compact "ends {Month} {Day}" label for an active promo's expiry. Formats
 * via `Date`/`toLocaleDateString` (fine for dates — the `parseFloat` ban is
 * a money-string rule, not a date rule). Pinned to `timeZone: "UTC"` so the
 * label is deterministic regardless of viewer/CI-runner local time — the
 * server always sends an instant, not a wall-clock date, and a local-tz
 * render would flip the displayed day near local midnight for some viewers.
 * Falls back to omitting the label for an unparseable ISO string rather
 * than rendering "Invalid Date".
 */
function formatPromoExpiry(expiresAt: string): string | null {
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return null;
  return `ends ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}`;
}

export const PriceBadge = ({ resolvedPrice }: PriceBadgeProps) => {
  if (!resolvedPrice) return null;

  const { tier, tierLabel, amountMonthly, currency, promo } = resolvedPrice;
  const promoExpiry = promo ? formatPromoExpiry(promo.expiresAt) : null;

  return (
    <span className="flex items-center gap-1.5 text-xs" data-testid="price-badge">
      {promo ? (
        <>
          <span className="text-slate-500 line-through">
            {formatMoneyFromString(amountMonthly, currency)}/mo
          </span>
          <span className="font-semibold text-emerald-400">
            {formatMoneyFromString(promo.amountAfterPromo, currency)}/mo
          </span>
          <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-400">
            {promo.code}
          </span>
          {promoExpiry && <span className="text-slate-500">{promoExpiry}</span>}
        </>
      ) : (
        <span className="font-semibold text-white" data-tier={tier}>
          {formatMoneyFromString(amountMonthly, currency)}/mo
        </span>
      )}
      <span className="text-slate-500">{tierLabel}</span>
    </span>
  );
};
